import React, { useEffect, useState } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from '~/timeInit';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import { DAY_FORMAT, type InvoiceMonth, currentDay, firstDayOfNextMonth, formatMonthLabel } from './monthRange';

interface Props {
  open: boolean;
  month: InvoiceMonth;
  partnerName: string;
  onCancel: () => void;
  /** Called with the details to stamp on the document, which is then printed. */
  onConfirm: (details: { invoiceNumber: string; invoiceDate: string; dueDate: string }) => void;
}

/**
 * Collects the details that are not derivable before an invoice is generated.
 *
 * The number is typed rather than minted: nothing in the service issues or reserves invoice
 * numbers, so Finance supplies the one they are raising it under in Xero. The due date defaults to
 * the first of next month — how these are actually billed — but is editable, since a partner on
 * different terms is settled commercially rather than in code.
 */
export default function GenerateInvoiceDialog({ open, month, partnerName, onCancel, onConfirm }: Props) {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dueDate, setDueDate] = useState<Dayjs>(() => dayjs(firstDayOfNextMonth()));

  // Reset each time the dialog opens, so a number from a previous invoice is never carried on to
  // a different one — the single most damaging thing that could silently happen here.
  useEffect(() => {
    if (open) {
      setInvoiceNumber('');
      setDueDate(dayjs(firstDayOfNextMonth()));
    }
  }, [open]);

  const trimmedNumber = invoiceNumber.trim();
  const isValid = trimmedNumber !== '' && dueDate.isValid();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Generate invoice</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {partnerName || 'This partner'} — {formatMonthLabel(month)}
        </Typography>

        <Stack spacing={2.5} mt={2}>
          <TextField
            autoFocus
            size="small"
            label="Invoice number"
            required
            value={invoiceNumber}
            onChange={(event) => setInvoiceNumber(event.target.value)}
            placeholder="INV-2483"
            helperText="The number you are raising this under in Xero"
            fullWidth
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due date"
              value={dueDate}
              onChange={(next) => next && setDueDate(next)}
              format="D MMM YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  helperText: 'Defaults to the first of next month',
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!isValid}
          // The invoice is dated the day it is raised, which is this moment.
          onClick={() =>
            onConfirm({ invoiceNumber: trimmedNumber, invoiceDate: currentDay(), dueDate: dueDate.format(DAY_FORMAT) })
          }
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
